// filepath: apps/web/src/features/admin/recordPermissions/types.ts
//
// FR-UI-ADM-2 — TypeScript wire-type mirrors for the record-level
// + field-level permission editor. Mirrors the Go wire types in
//   internal/kernel/record_field_permission_admin.go
// (RecordRuleResponse, FieldPermissionsReadResponse,
// EntityFieldOwnerResponse).
//
// INV-9: all server-supplied strings render as text nodes only.
//
// INV-1: TanStack Query cache keys include tenantSlug as 2nd
// segment — see queries.ts:adminKeys.recordPermissions /
// adminKeys.fieldPermissions / adminKeys.entityFieldOwner.

/**
 * FieldPermissionState — UI-side three-state enum (AC-3).
 *
 *   - "readable"  — can_read=true,  can_write=false
 *   - "writable"  — can_read=true,  can_write=true  (FR-AUTH-6 AC-7 invariant)
 *   - "hidden"    — represented by row absence on the server
 *
 * The "hidden" state is NOT a wire enum value — the SPA encodes it
 * by OMITTING the field from the grants array. The server
 * RoleStoreDB.UpsertFieldPermissionsTx full-set-replace semantics
 * DELETEs rows for fields not in the grants array (mirrors FR-AUTH-6
 * implementation note).
 *
 * The DATA-STATE ATTRIBUTE carries the canonical value
 * (`readable | writable | hidden`) — see FieldPermissionsDialog.tsx.
 */
export type FieldPermissionState = "readable" | "writable" | "hidden";

/**
 * Server wire-shape: `data-state` attribute value.
 */
export const FIELD_PERMISSION_DATA_STATE: Record<FieldPermissionState, string> =
  {
    readable: "readable",
    writable: "writable",
    hidden: "hidden",
  };

/**
 * FieldPermissionGrant — read+write projection per field. The
 * editor's three-state toggle grid renders one row per grant.
 */
export type FieldPermissionGrant = {
  field: string;
  can_read: boolean;
  can_write: boolean;
};

/**
 * RecordRuleResponse — GET /api/admin/roles/{roleName}/record-rule/{entityType}
 * wire shape. Mirrors RecordRuleResponse in
 * internal/kernel/record_field_permission_admin.go.
 *
 * - ownership_rule / condition_rule: nil when no rule of that kind
 *   is configured for this (role, entity) pair.
 * - rule_kind mirror: "ownership" | "condition" — used in PUT body.
 */
export type RecordRuleResponse = {
  role_name: string;
  entity_type: string;
  ownership_rule: string | null;
  condition_rule: string | null;
};

/**
 * FieldPermissionsReadResponse — GET /api/admin/roles/{roleName}/field-permissions/{entityType}
 * wire shape.
 */
export type FieldPermissionsReadResponse = {
  role_name: string;
  entity_type: string;
  version: number;
  grants: FieldPermissionGrant[];
};

/**
 * EntityFieldOwnerResponse — GET /api/admin/entity-field-owners/{entityType}
 * wire shape.
 */
export type EntityFieldOwnerResponse = {
  entity_type: string;
  owner_field: string | null;
  version: number;
};

/**
 * RecordPermissionConflict — typed envelope for the 409 conflict
 * surface (FR-UI-CORE-6). Mirrors VersionConflictError shape from
 * PermissionsMatrixPage (the matrix editor sibling).
 */
export type RecordPermissionConflict = {
  role_name: string;
  entity_type: string;
  current_version: number;
  server_grants?: FieldPermissionGrant[];
  server_ownership_rule?: string | null;
  server_condition_rule?: string | null;
  server_owner_field?: string | null;
};

/**
 * Fetch error envelope — maps the 5 FR-UI-CORE-10 fetch error
 * states. Used by the page to render the dedicated error
 * component per AC-8.
 */
export type RecordPermissionFetchError = {
  kind:
    | "fetch_failure"
    | "permission_denied"
    | "rate_limit"
    | "stale_version"
    | "network";
  message: string;
  retriable: boolean;
};
